// ============= Imports =============
import React, { useState } from 'react';
import { Button, Modal } from '@/ui';
import { CREATE_PROMPT_MESSAGES } from '@/constants/visualPrompts/createPromptMessages';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';
import { deleteCustomVisualPrompt } from '@/utils/visualPrompts/customVisualPromptStorage';
import { clearSavedChoicesForPrompt } from '@/utils/visualPrompts/visualPromptStorage';
import type { VisualPromptDefinition } from '@/types/visualPrompts/visualPrompt.types';
import { Body, Footer, PromptName } from './styles';

export interface DeleteVisualPromptModalProps {
  definition: VisualPromptDefinition | null;
  onClose: () => void;
  onDeleted: () => void;
}

// ============= Component =============
export const DeleteVisualPromptModal: React.FC<DeleteVisualPromptModalProps> = ({
  definition,
  onClose,
  onDeleted,
}) => {
  const { handleSuccess, handleError } = useWorkspaceErrorHandler();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = () => {
    if (!definition) {
      return;
    }
    setIsDeleting(true);
    try {
      deleteCustomVisualPrompt(definition.id);
      clearSavedChoicesForPrompt(definition.id);
      handleSuccess(CREATE_PROMPT_MESSAGES.deleteSuccess);
      onDeleted();
      onClose();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('[DeleteVisualPromptModal] Delete Failed:', error);
      handleError(error, CREATE_PROMPT_MESSAGES.deleteError);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal
      isOpen={!!definition}
      onClose={onClose}
      title={CREATE_PROMPT_MESSAGES.deleteModalTitle}
      size="small"
      footer={
        <Footer>
          <Button variant="ghost" onClick={onClose} disabled={isDeleting}>
            {CREATE_PROMPT_MESSAGES.deleteCancel}
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={isDeleting}>
            {CREATE_PROMPT_MESSAGES.deleteConfirm}
          </Button>
        </Footer>
      }
    >
      <Body>{CREATE_PROMPT_MESSAGES.deleteModalBody}</Body>
      {definition && <PromptName>{definition.title}</PromptName>}
    </Modal>
  );
};
